// src/backendServices/liveStreamManagment/api_recording.ts
// v2.0 — Les appels passent par Symfony (BDD + mediamtx-manager)
//         au lieu d'appeler le mediamtx-manager directement.

import axios from 'axios';
import { API_URLS } from '../urls';
import { estSansLimite } from '../../dataTypes/adminManagment/quota';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StartRecordingParams {
  channelUuid : string;
  channelName : string;
  filename    : string;   // Ex: "Lovelight-TV_2026-04-14-13-00-42"
  adminId     : number;   // Admin propriétaire de la session (crédité du quota au stop)
  format?     : string;   // "mp4" par défaut
  sessionName?: string;   // Nom saisi par l'opérateur dans la boîte "Enregistrement d'une session"
  angles?     : RecordingAngle[];  // Multi-angle : une entrée par source cochée du deck
}

/**
 * Un angle d'un enregistrement multi-angle : la source du deck à enregistrer, le nom de
 * fichier qui lui est attribué (slug de « {session} - {source} ») et l'identifiant du path
 * MediaMTX éphémère (`src_{uuid}_{angleId}`) sur lequel la régie publie cette source.
 *
 * `angleId` est contraint à [A-Za-z0-9-] — l'underscore rendrait le découpage
 * uuid/identifiant du nom de path ambigu, côté manager comme côté admin-api. Le préfixe
 * « ang » lui est réservé : un device qui porterait le même nom entrerait en collision
 * avec son path source SFU.
 */
export interface RecordingAngle {
  angleId    : string;
  sourceId   : string;
  sourceName : string;
  filename   : string;
  /**
   * Path MediaMTX déjà existant pour cette source (playlist `video_{uuid}`, entrée
   * OBS/RTMP `live_{uuid}`). Présent = la source vient déjà de MediaMTX : on enregistre
   * le path d'origine, la régie ne republie rien. Zéro flux montant, zéro encodage — et
   * le seul chemin qui marche pour une source que le navigateur ne sait pas capturer.
   */
  existingPath?: string;
}

export interface StopRecordingParams {
  channelUuid : string;
  sessionId   : number;   // ID BDD retourné par startRecording
}

export interface StartRecordingResult {
  sessionId : number;
  filename  : string;
  /** Multi-angle : une entrée par angle réellement démarré, avec sa session en BDD. */
  angles    : { angleId: string; sourceName: string; filename: string; sessionId: number }[];
}

// ─── Génération du nom de fichier ────────────────────────────────────────────

/**
 * Génère le nom de fichier pour la session d'enregistrement.
 * Format : {channelName}_{YYYY-MM-DD-HH-MM-SS}
 * Les caractères interdits dans un nom de fichier sont remplacés par _
 *
 * Le marqueur « Session-Regie » a été retiré le 25/08/2026 : il ne distinguait
 * rien — tout ce qui est enregistré ici vient de la régie — et prenait la place
 * qui manque au nom de la caméra dans les listes.
 *
 * La précision à la seconde est nécessaire : à la minute près, deux prises démarrées
 * rapidement (moins d'une minute d'écart) produisent le même nom de fichier — côté
 * mediamtx-manager, le renommage de fin d'enregistrement (os.rename) écrase alors
 * silencieusement le fichier de la première prise avec celui de la seconde.
 */
export function buildRecordingFilename(channelName: string): string {
  const now = new Date();

  const pad = (n: number) => String(n).padStart(2, '0');

  const datePart = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
  ].join('-');

  const timePart = [
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('-');

  const safeName = channelName
    .replace(/[/\\:*?"<>|]/g, '_')  // Caractères interdits filesystem
    .trim()
    .replace(/\s+/g, '-'); // Remplace espaces (ou groupes d'espaces) par '-'

  return `${safeName}_${datePart}-${timePart}`;
}

// ─── Provisioning (avant WebRTC) ───────────────────────────────────────────────

/**
 * Assure que la chaîne est au moins provisionnée en mode "restricted" dans MediaMtx
 * (webrtc_UUID doit exister) — à appeler AVANT de démarrer la connexion WebRTC régie et
 * bien avant `startRecording()`. Sans ça, `startRecording()` provisionnerait la chaîne
 * elle-même mais déclencherait aussitôt le PATCH record:true, avant même que le flux
 * WebRTC n'ait commencé à se connecter — ce qui fait échouer le renommage côté
 * mediamtx-manager (fenêtre de résolution de 5s dépassée par la négociation WebRTC).
 */
export const ensureChannelProvisionedForRecording = async (channelUuid: string): Promise<{ channelStatus: string }> => {
  const url = API_URLS.ensureChannelProvisionedForRecording.replace('{channelUuid}', channelUuid);

  const response = await axios.post(url, new FormData());

  if (response.data?.code !== 1) {
    throw new Error(response.data?.comment ?? 'Erreur préparation de la chaîne pour l\'enregistrement');
  }

  return { channelStatus: response.data.channelStatus };
};

// ─── Start ────────────────────────────────────────────────────────────────────

export const startRecording = async (params: StartRecordingParams): Promise<StartRecordingResult> => {
  const { channelUuid, channelName, filename, adminId, format = 'mp4', sessionName, angles } = params;

  const url = API_URLS.startChannelRecordingSession.replace('{channelUuid}', channelUuid);

  const formData = new FormData();
  formData.append('channelUuid', channelUuid);
  formData.append('channelName', channelName);
  formData.append('filename',    filename);
  formData.append('adminId',     String(adminId));
  formData.append('format',      format);
  if (sessionName) formData.append('sessionName', sessionName);

  // Format attendu par admin-api / le mediamtx-manager : angle_id en snake_case, et
  // sourceId (identifiant de source côté régie) volontairement omis — il n'a aucun sens
  // au-delà du navigateur.
  if (angles?.length) {
    formData.append('angles', JSON.stringify(angles.map(a => ({
      angle_id:   a.angleId,
      filename:   a.filename,
      sourceName: a.sourceName,
      ...(a.existingPath ? { path: a.existingPath } : {}),
    }))));
  }

  let response;

  try {
    response = await axios.post(url, formData);
  } catch (error) {
    // Un refus amont (quota atteint, chaîne en transition…) arrive en 4xx/5xx : axios
    // rejette AVANT même de lire le corps JSON — sans ce filet, le message précis du
    // backend (ex. quota de durée d'enregistrement) disparaît derrière un générique
    // "Request failed with status code 409".
    if (axios.isAxiosError(error) && error.response?.data?.comment) {
      throw new Error(error.response.data.comment);
    }
    throw error;
  }

  if (response.data?.code !== 1) {
    throw new Error(response.data?.comment ?? 'Erreur démarrage enregistrement');
  }

  return {
    sessionId : response.data.sessionId,
    filename  : response.data.filename,
    angles    : response.data.angles ?? [],
  };
};

// ─── Stop ─────────────────────────────────────────────────────────────────────

export const stopRecording = async (params: StopRecordingParams): Promise<void> => {
  const { channelUuid, sessionId } = params;

  const url = API_URLS.stopChannelRecordingSession.replace('{channelUuid}', channelUuid);

  const formData = new FormData();
  formData.append('channelUuid', channelUuid);
  formData.append('sessionId',   String(sessionId));

  const response = await axios.post(url, formData);

  if (response.data?.code !== 1) {
    throw new Error(response.data?.comment ?? 'Erreur arrêt enregistrement');
  }
};



// ─── Type session (retourné par Symfony) ─────────────────────────────────────
 
export interface RecordingSession {
  id                  : number;
  broadcastingChannel : { id: number; uuid: string; name: string };
  owner               : { id: number; email: string } | null;
  filename            : string;   // sans extension
  format              : string;   // "mp4" ou "ts"
  startedAt           : string;   // ISO 8601
  stoppedAt           : string | null;
  sizeBytes           : number | null;
  durationSeconds     : number | null;  // durée réelle du fichier (ffprobe), null si non calculée (session legacy ou échec ffprobe)
  /**
   * Où le fichier est réellement rangé, vu depuis le partage, et son adresse
   * publique. Les deux sont nulles ENSEMBLE, et leur nullité a un sens précis :
   * le fichier est encore sur zircon, donc à demander au mediamtx-manager.
   *
   * C'est le cas de tous les enregistrements antérieurs au déménagement.
   */
  storagePath?        : string | null;
  fileUrl?            : string | null;
  /** Commun aux fichiers d'une même prise multi-angle (programme + angles) ; null sinon. */
  takeId?             : string | null;
  takeProgram?        : boolean;
}

// ─── Durée en secondes (pour tri / formatage) ─────────────────────────────────
// Priorité à durationSeconds (ffprobe, exact). Le calcul par différence de dates
// (stoppedAt - startedAt) est un simple repli pour les sessions enregistrées avant
// l'introduction de ce champ : il est structurellement faux (inclut la latence
// réseau du bouton REC et le délai de finalisation MediaMTX côté serveur).
export function durationInSeconds(session: RecordingSession): number | null {
  if (session.durationSeconds !== null && session.durationSeconds !== undefined) {
    return session.durationSeconds;
  }
  if (!session.stoppedAt) return null;

  const startMs = new Date(session.startedAt).getTime();
  const stopMs  = new Date(session.stoppedAt).getTime();
  return Math.max(0, (stopMs - startMs) / 1000);
}

// ─── Durée formatée ────────────────────────────────────────────────────────────

export function computeDuration(session: RecordingSession): string {
  if (!session.stoppedAt) return 'En cours…';

  const totalSecExact = durationInSeconds(session);
  if (totalSecExact === null) return '—';

  const totalSec = Math.floor(totalSecExact);
  const h  = Math.floor(totalSec / 3600);
  const m  = Math.floor((totalSec % 3600) / 60);
  const s  = totalSec % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
 
// ─── Taille formatée ──────────────────────────────────────────────────────────
 
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—';
  if (bytes < 1024 * 1024)          return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024)   return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}
 
// ─── Estimation de la durée d'enregistrement restante (quota disque) ──────────
//
// (quotaDisk - quotaUsed), en octets, converti en secondes via un débit octets/s.
// Ce débit est mesuré empiriquement sur l'historique de sessions de l'admin
// (sizeBytes / durationSeconds, moyenné) dès qu'il y a assez d'échantillons ;
// sinon on retombe sur le débit théorique tiré des bitrates configurés
// (videoBitrate + audioBitrate). Fiabilisé par le forçage de la résolution
// WebRTC (degradationPreference: 'maintain-resolution', cf. api_whip.ts) :
// sans ça, une résolution qui varie en cours de route ferait dériver le débit
// réel loin de sa moyenne historique ou de sa valeur configurée.

export const MIN_SESSIONS_FOR_EMPIRICAL_BITRATE = 3;

/**
 * Débit moyen (octets/s) mesuré sur les sessions terminées de cet admin.
 * Retourne null si l'historique est insuffisant — l'appelant doit alors
 * retomber sur configuredBytesPerSecond().
 */
export function empiricalBytesPerSecond(sessions: RecordingSession[], adminId: number): number | null {
  const usable = sessions.filter(s =>
    s.owner?.id === adminId &&
    s.sizeBytes       !== null && s.sizeBytes       > 0 &&
    s.durationSeconds !== null && s.durationSeconds > 0
  );

  if (usable.length < MIN_SESSIONS_FOR_EMPIRICAL_BITRATE) return null;

  const total = usable.reduce((sum, s) => sum + (s.sizeBytes as number) / (s.durationSeconds as number), 0);
  return total / usable.length;
}

/** Débit théorique (octets/s) à partir des bitrates vidéo/audio configurés (en kbps). */
export function configuredBytesPerSecond(videoBitrateKbps: number, audioBitrateKbps: number): number {
  return ((videoBitrateKbps + audioBitrateKbps) * 1000) / 8;
}

/**
 * Durée d'enregistrement restante estimée (secondes) avant d'atteindre le quota disque.
 * null si le débit est nul/inconnu (pas d'estimation possible), ou si le quota disque
 * est illimité (SANS_LIMITE = -1, cf. dataTypes/adminManagment/quota.ts) — un compte
 * sans limite n'a pas de compte à rebours à afficher, et ne doit ni bloquer le
 * démarrage ni déclencher l'arrêt automatique côté LiveStreamManager.
 */
export function estimateRemainingRecordingSeconds(
  quotaDiskBytes: number,
  quotaUsedBytes: number,
  bytesPerSecond: number,
): number | null {
  if (estSansLimite(quotaDiskBytes)) return null;
  if (!bytesPerSecond || bytesPerSecond <= 0) return null;
  const remainingBytes = quotaDiskBytes - quotaUsedBytes;
  return remainingBytes <= 0 ? 0 : remainingBytes / bytesPerSecond;
}

/**
 * Durée restante ramenée au nombre de flux enregistrés SIMULTANÉMENT.
 *
 * En multi-angle, chaque angle coché écrit son propre fichier à côté du programme :
 * le quota disque part donc autant de fois plus vite, et une estimation calculée sur
 * le seul débit du programme promettrait N fois trop de temps.
 *
 * L'approximation assumée est que tous les fichiers pèsent le même débit que le
 * programme. C'est vrai des angles republiés par la régie (même encodeur, mêmes
 * réglages) ; ça l'est moins d'un angle enregistré à la source, dont le débit est
 * celui du flux d'origine et nous est inconnu ici. Une source native n'en consomme
 * pas moins du quota : elle compte donc comme les autres.
 */
export function remainingSecondsForStreams(remainingSec: number | null, streamCount: number): number | null {
  if (remainingSec === null) return null;
  return remainingSec / Math.max(1, streamCount);
}

/** Formate une durée restante estimée : "≈2h15", "≈45min", "Quota atteint", "—" (inconnue). */
export function formatRemainingDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds <= 0) return 'Quota atteint';

  const totalMin = Math.floor(seconds / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;

  return h > 0 ? `≈${h}h${String(m).padStart(2, '0')}` : `≈${Math.max(m, 1)}min`;
}

/** Formate une durée en secondes au format HH:MM:SS (zéro-paddé) — "—" si inconnue. */
export function formatSecondsAsClock(seconds: number | null): string {
  if (seconds === null) return '—';

  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// ─── URL de fichier (téléchargement ou visionnage) ────────────────────────────
 
/**
 * L'ANCIENNE VOIE : le fichier servi par le mediamtx-manager, depuis zircon.
 *
 * Elle ne vaut plus que pour les enregistrements qui n'ont pas déménagé — d'où
 * `resolveFileUrl()`, qui choisit. Elle disparaîtra avec le backend
 * `recording_<uuid>_backend` d'HAProxy, une fois les anciens fichiers migrés :
 * c'est la dernière voie où l'uuid interne d'une chaîne reste exposé.
 */
export function buildFileUrl(
  managerUrl : string,
  apiKey     : string,
  channelUuid: string,
  filename   : string,
  format     : string,
  mode       : 'attachment' | 'inline' = 'attachment'
): string {
  const fileWithExt = encodeURIComponent(`${filename}.${format}`);
  // Le mediamtx-manager est appelé via HAProxy : le front passe par
  // /{uuid}/metadata/ → microservice, mais pour les fichiers on passe
  // directement par l'URL du manager configurée dans settings.managerUrl.
  return `${managerUrl}/${channelUuid}/recording/files/${fileWithExt}?mode=${mode}`;
}

/**
 * L'adresse à utiliser pour CE fichier-là : celle du workingSpace quand il a
 * déménagé, celle de zircon sinon.
 *
 * ⚠️ L'adresse du workingSpace n'est jamais composée ici. Elle vient de
 * l'API, qui seule sait où le fichier a fini — un chemin recalculé côté
 * navigateur cesserait d'être vrai le jour où le rangement changerait.
 *
 * ⚠️ `?dl=1` N'EST PAS FACULTATIF pour un téléchargement. L'attribut `download`
 * d'un lien est ignoré dès que l'URL n'est pas de la même origine que la page —
 * et le serveur média n'est jamais de la même origine que le front. Sans ce
 * paramètre, le navigateur JOUE la vidéo au lieu de l'enregistrer. C'est nginx
 * qui pose alors `Content-Disposition: attachment`.
 */
export function resolveFileUrl(
  session    : RecordingSession,
  managerUrl : string,
  apiKey     : string,
  channelUuid: string,
  mode       : 'attachment' | 'inline' = 'attachment'
): string {
  if (session.fileUrl) {
    return mode === 'attachment' ? `${session.fileUrl}?dl=1` : session.fileUrl;
  }

  return buildFileUrl(managerUrl, apiKey, channelUuid, session.filename, session.format, mode);
}
 
// ─── Liste des sessions ───────────────────────────────────────────────────────
 
export const getRecordingSessions = async (channelUuid: string): Promise<RecordingSession[]> => {
  const url = API_URLS.listChannelRecordingSessions.replace('{channelUuid}', channelUuid);
  const response = await axios.get(url);
 
  console.log ( 'getRecordingSessions', url, response)
 
  if (response.data?.code !== 1) {
    throw new Error(response.data?.comment ?? 'Erreur récupération sessions');
  }
 
  return response.data.sessions as RecordingSession[];
};
 
// ─── Suppression ─────────────────────────────────────────────────────────────
 
export const deleteRecordingSession = async (
  channelUuid : string,
  sessionId   : number
): Promise<void> => {
  const url = API_URLS.deleteChannelRecordingSession
    .replace('{channelUuid}', channelUuid)
    .replace('{sessionId}',   String(sessionId));
 
  const response = await axios.delete(url);
 
  if (response.data?.code !== 1) {
    throw new Error(response.data?.comment ?? 'Erreur suppression session');
  }
};
 
// ─── Renommage ────────────────────────────────────────────────────────────────

/**
 * Alphabet autorisé pour le nom d'un enregistrement renommé : toute lettre
 * (accents compris — é, è, à, ç, œ…), chiffre, tiret et underscore. Ce qui
 * reste interdit, c'est ce qui casse un système de fichiers ou une URL prise
 * telle quelle : espace, `/ \ : * ? " < > |`, etc. Même alphabet côté serveur
 * (ChannelRecordingSessionRename) — ne jamais l'assouplir d'un seul côté.
 */
export const RECORDING_FILENAME_PATTERN = /^[\p{L}\p{N}_-]+$/u;

const RECORDING_FILENAME_FORBIDDEN_CHAR = /[^\p{L}\p{N}_-]/gu;

/** Retire au fil de la frappe tout caractère hors de RECORDING_FILENAME_PATTERN. */
export function sanitizeRecordingFilenameInput(value: string): string {
  return value.replace(RECORDING_FILENAME_FORBIDDEN_CHAR, '');
}

/** Caractères interdits présents dans `value`, sans doublon, dans l'ordre d'apparition. */
export function findForbiddenRecordingFilenameChars(value: string): string[] {
  const matches = value.match(RECORDING_FILENAME_FORBIDDEN_CHAR);
  return matches ? Array.from(new Set(matches)) : [];
}

export const renameRecordingSession = async (
  channelUuid : string,
  sessionId   : number,
  newFilename : string
): Promise<void> => {
  const url = API_URLS.renameChannelRecordingSession
    .replace('{channelUuid}', channelUuid)
    .replace('{sessionId}',   String(sessionId));

  const formData = new FormData();
  formData.append('filename', newFilename);

  let response;

  try {
    response = await axios.post(url, formData);
  } catch (error) {
    // Le contrôleur répond en 400/409/502 selon le refus (nom invalide, session en
    // cours, collision de nom, échec disque…) : axios rejette AVANT de lire le corps
    // JSON — sans ce filet, le message précis du backend disparaît derrière un
    // générique "Request failed with status code 409".
    if (axios.isAxiosError(error) && error.response?.data?.comment) {
      throw new Error(error.response.data.comment);
    }
    throw error;
  }

  if (response.data?.code !== 1) {
    throw new Error(response.data?.comment ?? 'Erreur renommage session');
  }
};

// ─── Slug et noms d'angles (boîte de dialogue « Enregistrement d'une session ») ──

/**
 * Translitère un libellé en slug de nom de fichier — même règle que le slugify()
 * d'admin-api (LegalStructureCreation) : accents dépliés, tout ce qui n'est ni
 * lettre ni chiffre devient un tiret, tirets compressés et rognés, minuscules.
 * Le résultat est sûr pour tous les systèmes de fichiers.
 */
export function slugifyRecordingName(text: string): string {
  const deaccented = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return deaccented
    .replace(/[^0-9A-Za-z]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

/**
 * Nettoie un nom de session pour en faire une base de nom de fichier, SANS le sluguifier :
 * on préserve la casse et les séparateurs du nom par défaut
 * (`Lovelight-TV_2026-08-18-15-23-12`), qui serait sinon aplati en
 * `lovelight-tv-session-regie-...` et deviendrait illisible.
 * Même traitement que buildRecordingFilename applique au nom de chaîne.
 */
export function sanitizeSessionName(name: string): string {
  return name
    .replace(/[/\\:*?"<>|]/g, '_')   // caractères interdits par le système de fichiers
    .trim()
    .replace(/\s+/g, '-');
}

/** Suffixe du fichier du programme en multi-angle, à la place d'un nom de caméra. */
export const FINAL_MIX_SUFFIX = 'FLUX-FINAL';

/**
 * Nom de fichier d'un angle : « {nom de la session}_{slug de la caméra} ».
 *
 * Seul le nom de la CAMÉRA est sluguifié — la base garde sa forme, sans quoi
 * l'horodatage et le nom de chaîne deviendraient méconnaissables dans la liste du
 * Stockage, où les fichiers d'une même prise doivent se ranger côte à côte.
 */
export function buildAngleFilename(sessionName: string, sourceName: string): string {
  return `${sanitizeSessionName(sessionName)}_${slugifyRecordingName(sourceName)}`;
}

/**
 * Nom de fichier du programme en multi-angle : « {nom de la session}_FLUX-FINAL ».
 * En enregistrement simple, le programme garde le nom de session nu — c'est le nom
 * historique, et il n'y a alors aucun autre fichier dont le distinguer.
 */
export function buildFinalMixFilename(sessionName: string): string {
  return `${sanitizeSessionName(sessionName)}_${FINAL_MIX_SUFFIX}`;
}

/** Une entrée d'angle par source à enregistrer, pour la boîte d'enregistrement et le REC du live. */
export function buildRecordingAngles(
  sources: { id: string; label: string; mediamtxPath?: string }[],
  sessionName: string,
): RecordingAngle[] {
  // L'identifiant d'angle nomme un path MediaMTX (`src_{uuid}_{angleId}`) : il doit
  // tenir dans [A-Za-z0-9-], ce que l'id de source côté régie ne garantit pas
  // (« remote:xxx », « dev:xxx »…). Un simple rang suffit — le path ne vit que le
  // temps de la prise, et le nom de fichier, lui, porte le vrai libellé.
  return sources.map((s, index) => ({
    angleId      : `ang${index + 1}`,
    sourceId     : s.id,
    sourceName   : s.label,
    filename     : buildAngleFilename(sessionName, s.label),
    existingPath : s.mediamtxPath,
  }));
}

/** Durée en secondes au format « 02h 15m 30s » — « --h --m --s » si inconnue. */
export function formatSecondsAsHms(seconds: number | null): string {
  if (seconds === null) return '--h --m --s';

  const total = Math.max(0, Math.floor(seconds));
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(Math.floor(total / 3600))}h ${pad(Math.floor((total % 3600) / 60))}m ${pad(total % 60)}s`;
}
